import { formatMoney } from '@domain';
import type { DeliveryResult } from '../api.js';
import type { Translate } from '../i18n/index.js';
import { theme } from '../theme.js';
import { Card } from '../components/ui.js';

/**
 * What this course made.
 *
 * The figure the whole training module exists to produce. Shown while the
 * cohort is still running as well as after it closes, because a course that
 * is losing money is worth knowing about before it finishes rather than
 * after.
 *
 * Both sides exclude tax, and cash is reported apart from both: a student
 * still owing their fee has not paid, but the course still earned it. See
 * api/src/domain/deliveryResult.ts.
 */

export function DeliveryResultCard({
  result,
  perSeat,
  closedAt,
  t,
}: {
  result: DeliveryResult;
  perSeat: number | null;
  closedAt: string | null;
  t: Translate;
}) {
  const made = result.result >= 0;

  /*
    The currency the figures are actually in, not the company's.

    They are the same in the ordinary case. They differ when a cohort was
    billed entirely in a foreign currency, which is reported in that
    currency rather than converted — see api/src/domain/deliveryResult.ts.
    Taking it from the result rather than from the organisation is what
    stops the card labelling USD figures as QAR.
  */
  const currency = result.currency;

  const figure = (
    title: string,
    value: string,
    hint?: string,
    colour?: string,
    strong = false,
  ) => (
    <div key={title}>
      <div style={{ fontSize: '11.5px', color: theme.textMuted, fontWeight: 600 }}>{title}</div>
      <div
        dir="ltr"
        style={{
          fontFamily: theme.fontMono,
          fontSize: strong ? '22px' : '18px',
          fontWeight: strong ? 700 : 600,
          color: colour ?? theme.text,
        }}
      >
        {value}
      </div>
      {hint && <div style={{ fontSize: '11px', color: theme.textFaint }}>{hint}</div>}
    </div>
  );

  return (
    <Card style={{ marginBottom: '14px' }}>
      <div
        style={{
          padding: '12px 16px',
          borderBottom: `1px solid ${theme.dividerSoft}`,
          fontWeight: 600,
          fontSize: '13px',
          background: theme.surfaceHeader,
          display: 'flex',
          justifyContent: 'space-between',
          gap: '10px',
          flexWrap: 'wrap',
        }}
      >
        <span>{t('What this course made')}</span>
        {closedAt && (
          <span style={{ fontWeight: 400, color: theme.textMuted, fontSize: '12px' }}>
            {t('Closed')} {closedAt.slice(0, 10)}
          </span>
        )}
      </div>

      <div
        style={{
          padding: '16px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '30px',
          alignItems: 'baseline',
        }}
      >
        {figure(
          t('Came in'),
          `${formatMoney(result.madeNet)} ${currency}`,
          `${result.invoiceCount} ${result.invoiceCount === 1 ? t('invoice') : t('invoices')}`,
        )}

        {figure(
          t('Went out'),
          `${formatMoney(result.spentNet)} ${currency}`,
          `${result.costCount} ${result.costCount === 1 ? t('cost') : t('costs')}`,
        )}

        {figure(
          made ? t('Made') : t('Lost'),
          `${formatMoney(Math.abs(result.result))} ${currency}`,
          result.marginPercent === null
            ? t('nothing billed yet')
            : `${result.marginPercent}% ${t('margin')}`,
          made ? theme.goodDeep : theme.badDeep,
          true,
        )}

        {perSeat !== null &&
          figure(
            made ? t('Per student') : t('Lost per student'),
            `${formatMoney(Math.abs(perSeat))} ${currency}`,
          )}
      </div>

      <div
        style={{
          padding: '13px 16px',
          borderTop: `1px solid ${theme.dividerSoft}`,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '24px',
          fontSize: '12.5px',
        }}
      >
        <span>
          <span style={{ color: theme.textMuted }}>{t('Collected')}: </span>
          <span dir="ltr" style={{ fontFamily: theme.fontMono }}>
            {formatMoney(result.collected)} {currency}
          </span>
        </span>
        {/*
          Shown only when there is one. Collected, credited and still to
          collect together account for the whole of what was invoiced, so a
          course with a withdrawal adds up where it otherwise would not.
        */}
        {result.credited > 0 && (
          <span>
            <span style={{ color: theme.textMuted }}>{t('Given back')}: </span>
            <span dir="ltr" style={{ fontFamily: theme.fontMono }}>
              {formatMoney(result.credited)} {currency}
            </span>
          </span>
        )}
        <span>
          <span style={{ color: theme.textMuted }}>{t('Still to collect')}: </span>
          <span
            dir="ltr"
            style={{
              fontFamily: theme.fontMono,
              fontWeight: result.outstanding > 0 ? 700 : 400,
              color: result.outstanding > 0 ? theme.badDeep : theme.text,
            }}
          >
            {formatMoney(result.outstanding)} {currency}
          </span>
        </span>
      </div>

      {/*
        Said plainly rather than left to be discovered. A figure this
        prominent gets quoted, and it should be quoted knowing what is in it.
      */}
      <div
        style={{
          padding: '11px 16px',
          borderTop: `1px solid ${theme.dividerSoft}`,
          fontSize: '11.5px',
          color: theme.textMuted,
          lineHeight: 1.7,
        }}
      >
        <div>
          {t('Both sides exclude tax. Only the invoices and bills put on this course are counted.')}
        </div>
        <div>
          {t('Salaries, rent and anything shared across the business are not here — those are in the profit and loss.')}
        </div>
        {result.creditedInvoices > 0 && (
          <div>
            {result.creditedInvoices}{' '}
            {result.creditedInvoices === 1
              ? t('invoice has a credit note against it, and what was given back has been taken off.')
              : t('invoices have credit notes against them, and what was given back has been taken off.')}
          </div>
        )}
        {result.draftInvoices > 0 && (
          <div style={{ color: theme.warnDeep, fontWeight: 600 }}>
            {result.draftInvoices}{' '}
            {result.draftInvoices === 1
              ? t('invoice is still a draft and is not counted.')
              : t('invoices are still drafts and are not counted.')}
          </div>
        )}

        {/*
          Said loudly, because this is the one case where the figures above
          are incomplete. Converting at Xero's rate was the alternative and
          was rejected: a silently inverted rate is worse than an honest
          gap. See api/src/domain/deliveryResult.ts.
        */}
        {result.otherCurrencies.length > 0 && (
          <div style={{ color: theme.warnDeep, fontWeight: 600 }}>
            {/* Currency and count as a labelled pair, each on its own
                line. Joining them into a clause would read backwards in
                Arabic, and the comma between them is a different character
                in each language. */}
            <div>{t('Not counted, because they are in another currency:')}</div>
            {result.otherCurrencies.map((other) => (
              <div key={other.currency} style={{ fontWeight: 400 }}>
                <span dir="ltr">{other.currency}</span>
                {' · '}
                {other.documents === 1 ? t('one document') : t('documents')}
                {other.documents === 1 ? '' : `: ${other.documents}`}
              </div>
            ))}
            <div style={{ fontWeight: 400 }}>
              {t('Convert them in Xero, or keep this course in one currency.')}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
